import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { Observable, combineLatest, map, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { itemsPerPageChanged, pageChanged } from '../../actions/feed.actions';
import { selectFeedCurrentPage, selectFeedItemsPerPage } from '../../reducers';

import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { FeedPostViewMode } from 'src/app/api/services/feed-api.service';
import { UserFacadeService } from '@services/user-facade.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { FeedPostsService } from '../../services/feed-posts.service';

@Component({
  selector: 'app-post-cards-tools',
  templateUrl: './post-cards-tools.component.html',
  styleUrls: ['./post-cards-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PostCardsToolsComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @ViewChild('pageInput') pageInput: ElementRef;
  @Input() toolsClass: String;
  @Input() viewMode: FeedPostViewMode;
  @Input() loading: boolean;

  /** When provided, these inputs drive the component instead of the ngrx store. */
  @Input() externalCurrentPage: number | null = null;
  @Input() externalItemsPerPage: number | null = null;
  @Input() externalTotalItemsCount: number | null = null;

  /** Emitted when the user changes the page. If not listened to, falls back to store dispatch. */
  @Output() externalPageChange = new EventEmitter<number>();
  /** Emitted when the user changes items per page. If not listened to, falls back to store dispatch. */
  @Output() externalItemsPerPageChange = new EventEmitter<number>();

  itemsPerPageOptions: number[] = [10, 20, 30, 50, 100, 150, 200, 250, 300];
  currentPage$: Observable<number>;
  totalItemsCount$: Observable<number>;
  itemsPerPage$: Observable<number>;

  AppComponents = AppFeatures;

  initialized = false;

  paginationData$: Observable<{
    page: number;
    pageSize: number;
    collectionSize: number;
  }>;

  private get isExternalMode(): boolean {
    return this.externalCurrentPage !== null;
  }

  protected destroyRef = inject(DestroyRef);
  private readonly feedPosts = inject(FeedPostsService);

  // `count_posts` is a field of the list response, so the store-driven branch reads
  // it off the feed's query. Built once here because `setupObservables()` runs from
  // ngOnInit/ngOnChanges, outside an injection context.
  private readonly feedTotalItemsCount$ = toObservable(
    computed(() => this.feedPosts.totalItemsCount()),
  );

  constructor(
    protected store: Store<AppState>,
    protected userFacade: UserFacadeService,
  ) {}

  ngOnInit(): void {
    this.setupObservables();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['externalCurrentPage'] ||
      changes['externalItemsPerPage'] ||
      changes['externalTotalItemsCount']
    ) {
      this.setupObservables();
    }
  }

  private setupObservables(): void {
    if (this.isExternalMode) {
      this.currentPage$ = of(this.externalCurrentPage);
      this.itemsPerPage$ = of(this.externalItemsPerPage ?? 30);
      this.totalItemsCount$ = of(this.externalTotalItemsCount ?? 0);
    } else {
      this.currentPage$ = this.store.select(selectFeedCurrentPage);
      this.itemsPerPage$ = this.store.select(selectFeedItemsPerPage);
      this.totalItemsCount$ = this.feedTotalItemsCount$;
    }

    this.paginationData$ = combineLatest([
      this.currentPage$,
      this.itemsPerPage$,
      this.totalItemsCount$,
    ]).pipe(
      map(([page, pageSize, collectionSize]) => ({
        page,
        pageSize,
        collectionSize,
      })),
    );
  }

  onItemsPerPageChange(value: number) {
    if (this.isExternalMode) {
      this.externalItemsPerPageChange.emit(value);
    } else {
      this.store.dispatch(itemsPerPageChanged({ itemsPerPage: value }));
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initialized = true;
    });
  }

  pageChanged(page: number): void {
    if (!this.initialized) {
      this.initialized = true;
      return;
    }
    this.doPageChange(page);
  }

  doPageChange(page: number | string): void {
    if (typeof page === 'string') {
      page = parseInt(page, 10) || 1;
    }
    if (this.isExternalMode) {
      this.externalPageChange.emit(page);
    } else {
      this.store.dispatch(pageChanged({ pageNumber: page }));
    }
  }
}
